import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Typography,
} from "@mui/joy";
import Divider from "@mui/joy/Divider";
import { toast } from "react-toastify";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { db } from "../../FirebaseConfig";
import Tooltip from "@mui/joy/Tooltip";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Switch from "@mui/joy/Switch";
import InfoIcon from "@mui/icons-material/Info";
import { Link } from "react-router-dom";
interface Props {
  data: any;
  rowsPerPage: number;
}

function generateSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\-]+/g, "") // Remove all non-word chars
    .replace(/\-\-+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
}

const PaginationTable: React.FC<Props> = ({ data, rowsPerPage }) => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(
    null
  );
  const [open, setOpen] = React.useState(false);
  const [registrationCounts, setRegistrationCounts] = React.useState<{
    [key: string]: number;
  }>({});
  const [currentRows, setCurrentRows] = React.useState(
    data.slice(0, rowsPerPage)
  );

  const totalPages = Math.ceil(data.length / rowsPerPage);

  React.useEffect(() => {
    const indexOfLastRow = (currentPage + 1) * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    setCurrentRows(data.slice(indexOfFirstRow, indexOfLastRow));
  }, [data, currentPage, rowsPerPage]);

  const requestDelete = (eventId: string) => {
    setSelectedEventId(eventId);
    setOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  React.useEffect(() => {
    const fetchAllRegistrations = async () => {
      let newRegistrationCounts: { [key: string]: number } = {};
      for (const row of data) {
        try {
          const registrationRef = db
            .collection("Events")
            .doc(row.eventname.toLowerCase())
            .collection("Registrations");
          const snapshot = await registrationRef.get();
          newRegistrationCounts[row.eventname] = snapshot.size;
        } catch (error) {
          toast.error("Error fetching registrations");
          newRegistrationCounts[row.eventname] = 0;
        }
      }
      setRegistrationCounts(newRegistrationCounts);
    };

    fetchAllRegistrations();
  }, [data]);

  const requestLive = (eventId: string) => {
    const updatedRows = currentRows.map((row: any) => {
      if (row.eventname === eventId) {
        return { ...row, live: !row.live };
      }
      return row;
    });
    setCurrentRows(updatedRows);
    handleLive(eventId);
  };

  const handleDelete = async () => {
    if (selectedEventId) {
      try {
        await db.collection("Events").doc(selectedEventId.toLowerCase()).delete();
        toast.success("Event deleted successfully");

        // Filter out the deleted event from currentRows
        const updatedRows = currentRows.filter(
          (row: any) => row.eventname !== selectedEventId
        );
        setCurrentRows(updatedRows); // Update the state to reflect the deletion

        setOpen(false);
      } catch (error) {
        toast.error("Error deleting event");
      }
    }
  };

  const handleLive = async (eventId: string) => {
    try {
      const eventRef = db.collection("Events").doc(eventId.toLowerCase());
      const docSnapshot = await eventRef.get();

      if (!docSnapshot.exists) {
        toast.error("Event not found");
        return;
      }

      const currentLiveStatus = docSnapshot.data()?.live;
      const newLiveStatus = !currentLiveStatus;

      await eventRef.update({ live: newLiveStatus });
      toast.success("Live status updated successfully");
    } catch (error) {
      toast.error("Error updating live status");
    }
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Sr. No
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Category
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Event Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Organizer
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Participants
              </th>
              <th
                style={{
                  width: "8%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Live
              </th>
              <th
                style={{
                  width: "8%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Info
              </th>
              <th
                style={{
                  width: "8%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Delete
              </th>
            </tr>
          </thead>

          <tbody>
            {currentRows &&
              currentRows.map((row: any, index: any) => (
                <tr key={row.id}>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">{index + 1}</Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.category ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.eventname ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.organizer ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {registrationCounts[row.eventname] ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Switch
                      color={row.live ? "success" : "danger"}
                      checked={row.live}
                      onChange={() => {
                        requestLive(row.eventname);
                      }}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Tooltip
                      title="View Details"
                      color="success"
                      placement="bottom"
                      variant="solid"
                    >
                      <Button
                        color="success"
                        size="sm"
                        variant="plain"
                        aria-label="Info"
                        component={Link}
                        to={`/manageevent/${generateSlug(row.eventname)}`}
                      >
                        <InfoIcon />
                      </Button>
                    </Tooltip>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Tooltip
                      title="Delete"
                      color="danger"
                      placement="bottom"
                      variant="solid"
                    >
                      <Button
                        size="sm"
                        color="danger"
                        variant="plain"
                        aria-label="Delete"
                        onClick={() => {
                          requestDelete(row.eventname);
                        }}
                      >
                        <DeleteForeverIcon />
                      </Button>
                    </Tooltip>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </Sheet>

      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          startDecorator={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>

        {Array.from({ length: totalPages }).map((_, index) => (
          <IconButton
            key={index}
            size="sm"
            variant={currentPage === index ? "outlined" : "plain"}
            color="neutral"
            onClick={() => handlePageChange(index)}
          >
            {index + 1}
          </IconButton>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange(currentPage + 1)}
          endDecorator={<KeyboardArrowRightIcon />}
        >
          Next
        </Button>
      </Box>

      <div>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>
              <WarningRoundedIcon />
              Confirmation
            </DialogTitle>
            <Divider />
            <DialogContent>
              Are you sure you want to delete this Event?
            </DialogContent>
            <DialogActions>
              <Button variant="solid" color="danger" onClick={handleDelete}>
                Delete
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </div>
    </>
  );
};

export default PaginationTable;
